import { useRouter } from 'next/navigation';
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Icon } from '../../../components/icons';
import { IconButton } from '../../../components/ui/icon-button';
import { Link } from '../../../components/ui/Link';
import { buttonVariants } from '../../../components/ui/button-variants';
import {
  trackMatchAnimalImpression,
  trackProfileLinkClick,
  type Ga4Surface,
} from '../../../lib/analytics';
import { cn } from '../../../lib/utils';
import type { ShelterAnimal } from '../types';
import { MatchPercentBadge } from './MatchPercentBadge';

export type MatchResultPetCardAnalytics = {
  species: 'Cat' | 'Dog';
  rank: number;
  surface: Ga4Surface;
  shelterId?: string | null;
};

export type MatchResultPetCardProps = {
  animal: ShelterAnimal;
  matchPercent: number;
  profileHref: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  className?: string;
  analytics?: MatchResultPetCardAnalytics;
};

export function MatchResultPetCard({
  animal,
  matchPercent,
  profileHref,
  isFavorite,
  onToggleFavorite,
  className,
  analytics,
}: MatchResultPetCardProps) {
  const router = useRouter();
  const impressionTracked = useRef(false);
  const photoAlt = animal.Name ? `${animal.Name} photo` : 'Pet photo';
  const isExternalProfile = /^https?:\/\//i.test(profileHref.trim());
  const buttonClass = cn(
    buttonVariants({ variant: 'primary', size: 'md', fullWidth: true }),
    'mt-auto no-underline',
  );

  useEffect(() => {
    if (!analytics || impressionTracked.current) return;
    impressionTracked.current = true;
    trackMatchAnimalImpression({
      species: analytics.species,
      animalId: animal.ID,
      rank: analytics.rank,
      matchPercent,
      surface: analytics.surface,
      shelterId: analytics.shelterId,
    });
  }, [analytics, animal.ID, matchPercent]);

  const trackProfileClick = () => {
    if (!analytics) return;
    trackProfileLinkClick({
      animalId: animal.ID,
      surface: analytics.surface,
      shelterId: analytics.shelterId,
    });
  };

  const openProfileFromCard = () => {
    trackProfileClick();

    if (isExternalProfile) {
      window.open(profileHref, '_blank', 'noopener,noreferrer');
      return;
    }
    router.push(profileHref);
  };

  const handleCardActivate = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    openProfileFromCard();
  };

  const profileCta = isExternalProfile ? (
    <a
      href={profileHref}
      target='_blank'
      rel='noopener noreferrer'
      className={buttonClass}
      onClick={(e) => {
        e.stopPropagation();
        trackProfileClick();
      }}
    >
      View profile
      <Icon name='arrow-right' className='size-4' />
    </a>
  ) : (
    <Link
      href={profileHref}
      className={buttonClass}
      onClick={(e) => {
        e.stopPropagation();
        trackProfileClick();
      }}
    >
      View profile
      <Icon name='arrow-right' className='size-4' />
    </Link>
  );

  return (
    <article
      className={cn(
        'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      {/* Square media slot so every card shares the same geometry in the grid. */}
      <div
        className='relative aspect-square w-full shrink-0 cursor-pointer bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
        role='button'
        tabIndex={0}
        aria-label={`View profile for ${animal.Name}`}
        onClick={openProfileFromCard}
        onKeyDown={handleCardActivate}
      >
        {animal.CoverPhoto ? (
          // eslint-disable-next-line @next/next/no-img-element -- shelter CDN URLs
          <img
            src={animal.CoverPhoto}
            alt={photoAlt}
            className='absolute inset-0 size-full object-cover'
          />
        ) : (
          <div
            className='flex size-full items-center justify-center text-sm text-muted-foreground'
            role='img'
            aria-label='No photo available'
          >
            No photo
          </div>
        )}
        <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-foreground/25 via-transparent to-transparent' />
        <div className='absolute right-2 top-2'>
          <MatchPercentBadge matchPercent={matchPercent} />
        </div>
        <div className='absolute bottom-2 right-2'>
          <IconButton
            type='button'
            variant='ghost'
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            aria-pressed={isFavorite}
            aria-label={
              isFavorite ? `Remove ${animal.Name} from favorites` : 'Favorite'
            }
            className={cn(
              'pointer-events-auto rounded-full border-0 bg-foreground/55 text-primary-foreground shadow-none backdrop-blur-sm hover:bg-foreground/65',
            )}
          >
            <Icon
              name={isFavorite ? 'heart-filled' : 'heart-outline'}
              className='size-5 text-primary-foreground'
            />
          </IconButton>
        </div>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-3 p-4'>
        <h3
          className='line-clamp-2 min-h-14 cursor-pointer font-heading text-lg font-bold leading-snug text-card-foreground'
          onClick={openProfileFromCard}
        >
          {animal.Name}
        </h3>
        <div onClick={(e) => e.stopPropagation()}>{profileCta}</div>
      </div>
    </article>
  );
}
